import React, {useState, useEffect, useRef} from "react"
import {Line} from "react-chartjs-2"
const api = {
  key: process.env.REACT_APP_WEATHER_API_KEY,
  base: "https://api.openweathermap.org/data/2.5/"
}

function App() {
  const [query, setQuery] = useState("")
  const [weather, setWeather] = useState({})
  const [daily, setDaily] = useState({})
  const [data, setData] = useState({})
  const [isDay, setIsDay] = useState(false)
  const [suggestions, setSuggestions] = useState([])
  const [isInvalid, setIsInvalid] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const searchBoxRef = useRef(null)

  // --- shared helper: fetch + set weather/data from lat/lon ---
  const fetchWeatherByCoords = (lat, lon) => {
    fetch(
      `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&cnt=7&units=metric&appid=${api.key}`
    )
      .then((result1) => result1.json())
      .then((result1) => {
        setDaily(result1)
        setWeather(result1)
        setIsDay(result1.dt >= result1.sys.sunrise && result1.dt < result1.sys.sunset);
        console.log("Onecall API is:", result1)

        const chartData = {
          labels: ["07h", "09h", "11h", "13h", "15h", "17h"],
          datasets: [
            {
              label: "Temperature",
              fill: true,
              tension: 0.4,
              backgroundColor: ["rgba(0,122,255,0.2)"],
              borderColor: ["rgb(0,122,255)"],
              borderWidth: 3,
              pointRadius: 6,
              pointBackgroundColor: ["rgba(0,122,255,1)"],
              pointBorderColor: ["rgba(0,122,255,0.2)"],
              pointBorderWidth: 5,
              data: [
                result1.main.temp,
                result1.main.temp,
                result1.main.temp,
                result1.main.temp,
                result1.main.temp,
                result1.main.temp,
                result1.main.temp,
              ]
            }
          ]
        }
        setData(chartData)
      })
  }

  // --- run once on first load: get current position and load its weather ---
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const {latitude, longitude} = position.coords
          fetchWeatherByCoords(latitude, longitude)
        },
        (error) => {
          console.log("Geolocation error:", error)
        }
      )
    } else {
      console.log("Geolocation not supported by this browser.")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isSelectedRef = useRef(false)

  // --- click outside handler for dropdown ---
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // --- Debounced location search & suggestion fetch (1000ms delay) ---
  useEffect(() => {
    if (isSelectedRef.current) {
      isSelectedRef.current = false
      return
    }

    if (!query.trim()) {
      setSuggestions([])
      setIsInvalid(false)
      setShowDropdown(false)
      return
    }

    const timer = setTimeout(() => {
      const trimmed = query.trim()
      const encodedQuery = encodeURIComponent(trimmed)

      // Open-Meteo Geocoding API provides real-time autocomplete for partial & spaced queries (e.g. "New Delh", "Karol Ba")
      fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodedQuery}&count=5&language=en&format=json`)
        .then((res) => res.json())
        .then((data) => {
          if (data && Array.isArray(data.results) && data.results.length > 0) {
            const formatted = data.results.map((loc) => ({
              name: loc.name,
              state: loc.admin1 || "",
              country: loc.country_code || loc.country || "",
              lat: loc.latitude,
              lon: loc.longitude
            }))
            setSuggestions(formatted)
            setIsInvalid(false)
            setShowDropdown(true)
          } else {
            // Secondary fallback check against OpenWeather weather API
            fetch(`${api.base}weather?q=${encodedQuery}&units=metric&APPID=${api.key}`)
              .then((res) => res.json())
              .then((result) => {
                if (result.cod !== 200 && result.cod !== "200") {
                  setSuggestions([])
                  setIsInvalid(true)
                  setShowDropdown(true)
                } else {
                  setSuggestions([
                    {
                      name: result.name,
                      state: "",
                      country: result.sys ? result.sys.country : "",
                      lat: result.coord.lat,
                      lon: result.coord.lon
                    }
                  ])
                  setIsInvalid(false)
                  setShowDropdown(true)
                }
              })
              .catch(() => {
                setSuggestions([])
                setIsInvalid(true)
                setShowDropdown(true)
              })
          }
        })
        .catch(() => {
          setSuggestions([])
          setIsInvalid(true)
          setShowDropdown(true)
        })
    }, 500)

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  const handleSelectSuggestion = (suggestion) => {
    const locationName = `${suggestion.name}${suggestion.state ? `, ${suggestion.state}` : ""}${suggestion.country ? `, ${suggestion.country}` : ""}`
    isSelectedRef.current = true
    setQuery(locationName)
    setShowDropdown(false)
    setIsInvalid(false)
    setSuggestions([])
    fetchWeatherByCoords(suggestion.lat, suggestion.lon)
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && query.trim()) {
      if (suggestions.length > 0) {
        handleSelectSuggestion(suggestions[0])
        return
      }

      const trimmed = query.trim()
      const encodedQuery = encodeURIComponent(trimmed)
      fetch(`${api.base}weather?q=${encodedQuery}&units=metric&APPID=${api.key}`)
        .then((res) => res.json())
        .then((result) => {
          if (result.cod !== 200 && result.cod !== "200") {
            setIsInvalid(true)
            setShowDropdown(true)
          } else {
            setIsInvalid(false)
            setShowDropdown(false)
            setWeather(result)
            if (result.coord) {
              fetchWeatherByCoords(result.coord.lat, result.coord.lon)
            }
          }
        })
        .catch(() => {
          setIsInvalid(true)
          setShowDropdown(true)
        })
    }
  }

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      }
    },
    interaction: {
      intersect: false,
      mode: "index"
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          // color: 'red',
          font: {
            weight: "bold"
          }
        }
      },
      y: {
        // title: {
        //   display: true,
        //   text: 'Value'
        // },
        display: false,
        // min: 20,
        // max: 30,
        ticks: {
          stepSize: 1
        }
      }
    }
  }
  const dateBuilder = (d) => {
    let months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December"
    ]
    let days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thrusday",
      "Friday",
      "Saturday"
    ]

    let day = days[d.getDay()]
    let date = d.getDate()
    let month = months[d.getMonth()]
    let year = d.getFullYear().toString().substr(-2)

    return `${day} | ${date} ${month} '${year}`
  }
  const dateBuilder1 = (d) => {
    let days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    let day = d.getDay()
    if (day < 6) {
      day = day + 1
    } else {
      day = 0
    }
    let day1 = days[day]
    return `${day1}`
  }
  const dateBuilder2 = (d) => {
    let days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    let day = d.getDay()
    if (day < 5) {
      day = day + 2
    } else if (day > 5) {
      day = day - 5
    } else if (day === 5) {
      day = 0
    }
    let day2 = days[day]
    return `${day2}`
  }
  const dateBuilder3 = (d) => {
    let days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    let day = d.getDay()
    if (day < 4) {
      day = day + 3
    } else if (day > 4) {
      day = day - 4
    } else if (day === 4) {
      day = 0
    }
    let day3 = days[day]
    return `${day3}`
  }
  const dateBuilder4 = (d) => {
    let days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    let day = d.getDay()
    if (day < 3) {
      day = day + 4
    } else if (day > 3) {
      day = day - 3
    } else if (day === 3) {
      day = 0
    }
    let day4 = days[day]
    return `${day4}`
  }
  const dateBuilder5 = (d) => {
    let days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    let day = d.getDay()
    if (day < 2) {
      day = day + 5
    } else if (day > 2) {
      day = day - 2
    } else if (day === 2) {
      day = 0
    }
    let day5 = days[day]
    return `${day5}`
  }
  const dateBuilder6 = (d) => {
    let days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    let day = d.getDay()
    if (day < 1) {
      day = day + 6
    } else {
      day = day - 1
    }
    let day6 = days[day]
    return `${day6}`
  }

  return (
    <div className="app">
      <div className="wrapper">
        <div className="topicon">
          {typeof weather.main !== "undefined" ? (
            weather.weather[0].icon === "50d" ||
            weather.weather[0].icon === "50n" ||
            weather.weather[0].main === "Clouds" ||
            weather.weather[0].main === "Snow" ? !isDay ? (
              <svg
                viewBox="0 0 940 940"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="cloud"
              >
                <g mask="url(#mask0)">
                  <path
                    d="M151.907 694.733C135.682 651.411 135.682 587.332 135.682 587.332L151.907 376.14L216.036 217.295L737.568 191.122H907.549L940 431.195L794.744 544.011C794.744 544.011 681.939 812.964 665.713 831.014C649.487 849.065 470.234 942.025 426.194 938.415C382.154 934.805 168.132 738.054 151.907 694.733Z"
                    fill="#C4C4C4"
                    fillOpacity="0.2"
                  />
                  <path
                    d="M16.0532 382.742C31.757 338.209 71.7131 291.144 71.7131 291.144L214.71 148.746L358.455 82.3341L738.287 471.813L856.765 605.02L729.688 806.777L558.097 775.805C558.097 775.805 311.767 884.943 289.202 885.485C266.637 886.027 83.7318 813.831 55.2866 776.667C26.8414 739.503 0.34946 427.275 16.0532 382.742Z"
                    fill="#C4C4C4"
                    fillOpacity="0.2"
                  />
                  <path
                    d="M82.1755 260.152C106.795 222.581 154.789 191.594 154.789 191.594L320.791 106.092L470.694 94.9803L741.864 616.636L823.856 790.783L659.702 940.12L505.141 845.858C505.141 845.858 249.29 860.345 227.944 852.45C206.598 844.555 50.5097 705.863 31.9704 658.998C13.431 612.133 57.5555 297.724 82.1755 260.152Z"
                    fill="#C4C4C4"
                    fillOpacity="0.2"
                  />
                </g>
              </svg>
            )
            : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 940 940" fill="none" className="sunnyClouds">
<g clip-path="url(#clip0_310_558)">
<path d="M-311.576 809.076C-355.447 773.037 -390.564 705.973 -390.564 705.973L-486.167 475.639L-493.645 272.631L139.108 -53.7227L350.013 -151.163L521.84 81.4939L403.437 282.833C403.437 282.833 410.86 628.981 400.62 657.175C390.379 685.368 218.913 885.413 162.291 906.881C105.669 928.349 -267.703 845.117 -311.576 809.076Z" fill="white" fill-opacity="0.2"/>
<path d="M438.237 845.624C365.485 857.848 254.629 830.767 254.629 830.767L-112.914 710.965L-396.368 523.123L-512.072 -469.561L-535.024 -789.495L-124.089 -749.106L90.6918 -428.023C90.6918 -428.023 571.202 -102.027 604.621 -63.8553C638.039 -25.6837 823.058 350.991 822.761 432.358C822.463 513.724 510.992 833.395 438.237 845.624Z" fill="white" fill-opacity="0.2"/>
<g filter="url(#filter0_d_310_558)">
<path d="M115.212 671.424C270.521 671.424 396.424 545.521 396.424 390.212C396.424 234.903 270.521 109 115.212 109C-40.0971 109 -166 234.903 -166 390.212C-166 545.521 -40.0971 671.424 115.212 671.424Z" fill="#FFC75B" fill-opacity="0.82"/>
</g>
<path d="M330.815 659.657C280.09 642.069 227.13 590.64 227.13 590.64L67.477 403.982L-4.87558 208.616L452.864 -364.516L609.104 -544.473L837.372 -386.17L797.108 -141.853C797.108 -141.853 915.735 193.403 915.743 225.066C915.746 256.732 827.823 521.101 784.36 564.827C740.895 608.557 381.54 677.243 330.815 659.657Z" fill="white" fill-opacity="0.2"/>
<path d="M-226.005 350.465C-252.472 303.027 -258.045 229.691 -258.045 229.691L-253.702 -14.1554L-177.789 -204.422L549.612 -303.278L787.432 -325.736L853.705 -55.2689L660.284 93.0384C660.284 93.0384 525.839 415.749 504.707 438.551C483.577 461.354 240.865 591.427 178.933 593.113C117.003 594.802 -199.541 397.902 -226.005 350.465Z" fill="white" fill-opacity="0.2"/>
</g>
<defs>
<filter id="filter0_d_310_558" x="-186" y="93" width="602.424" height="602.424" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
<feFlood flood-opacity="0" result="BackgroundImageFix"/>
<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
<feOffset dy="4"/>
<feGaussianBlur stdDeviation="10"/>
<feComposite in2="hardAlpha" operator="out"/>
<feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
<feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_310_558"/>
<feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_310_558" result="shape"/>
</filter>
<clipPath id="clip0_310_558">
<rect width="940" height="940" fill="white"/>
</clipPath>
</defs>
</svg>
            )
            : weather.weather[0].main === "Drizzle" ||
              weather.weather[0].main === "Rain" ||
              weather.weather[0].main === "Thunderstorm" ? (
              <svg
                viewBox="0 0 940 940"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="rain"
              >
                <rect
                  width="3.53709"
                  height="148.503"
                  rx="1.76854"
                  transform="matrix(-0.888791 -0.458312 -0.388549 0.921428 344.893 534.123)"
                  fill="url(#paint0_linear)"
                  fillOpacity="0.4"
                />
                <rect
                  width="3.53709"
                  height="148.503"
                  rx="1.76854"
                  transform="matrix(-0.888791 -0.458312 -0.388549 0.921428 562.724 742.407)"
                  fill="url(#paint1_linear)"
                  fillOpacity="0.4"
                />
                <rect
                  width="3.53709"
                  height="148.503"
                  rx="1.76854"
                  transform="matrix(-0.888792 -0.458312 -0.388549 0.921428 266.277 642.793)"
                  fill="url(#paint2_linear)"
                  fillOpacity="0.4"
                />
                <rect
                  width="3.53709"
                  height="148.503"
                  rx="1.76854"
                  transform="matrix(-0.888792 -0.458312 -0.388549 0.921428 266.277 534.123)"
                  fill="url(#paint3_linear)"
                  fillOpacity="0.4"
                />
                <rect
                  width="3.53709"
                  height="148.503"
                  rx="1.76854"
                  transform="matrix(-0.888792 -0.458312 -0.388549 0.921428 405.493 597.514)"
                  fill="url(#paint4_linear)"
                  fillOpacity="0.4"
                />
                <rect
                  width="3.53709"
                  height="148.503"
                  rx="1.76854"
                  transform="matrix(-0.888791 -0.458312 -0.388549 0.921428 303.947 731.54)"
                  fill="url(#paint5_linear)"
                  fillOpacity="0.4"
                />
                <rect
                  width="3.53709"
                  height="148.503"
                  rx="1.76854"
                  transform="matrix(-0.888791 -0.458312 -0.388549 0.921428 190.937 577.591)"
                  fill="url(#paint6_linear)"
                  fillOpacity="0.4"
                />
                <rect
                  width="3.53709"
                  height="148.503"
                  rx="1.76854"
                  transform="matrix(-0.888791 -0.458312 -0.388549 0.921428 110.683 635.548)"
                  fill="url(#paint7_linear)"
                  fillOpacity="0.4"
                />
                <rect
                  width="3.53709"
                  height="148.503"
                  rx="1.76854"
                  transform="matrix(-0.888791 -0.458312 -0.388549 0.921428 100.856 497.9)"
                  fill="url(#paint8_linear)"
                  fillOpacity="0.4"
                />
                <rect
                  width="3.53709"
                  height="148.503"
                  rx="1.76854"
                  transform="matrix(-0.888792 -0.458312 -0.388549 0.921428 467.73 572.158)"
                  fill="url(#paint9_linear)"
                  fillOpacity="0.4"
                />
                <rect
                  width="3.53709"
                  height="148.503"
                  rx="1.76854"
                  transform="matrix(-0.888791 -0.458312 -0.388549 0.921428 541.433 472.544)"
                  fill="url(#paint10_linear)"
                  fillOpacity="0.4"
                />
                <rect
                  width="3.53709"
                  height="148.503"
                  rx="1.76854"
                  transform="matrix(-0.888792 -0.458312 -0.388549 0.921428 567.638 499.711)"
                  fill="url(#paint11_linear)"
                  fillOpacity="0.4"
                />
                <rect
                  width="3.53709"
                  height="148.503"
                  rx="1.76854"
                  transform="matrix(-0.888792 -0.458312 -0.388549 0.921428 469.368 684.45)"
                  fill="url(#paint12_linear)"
                  fillOpacity="0.4"
                />
                <rect
                  width="3.53709"
                  height="148.503"
                  rx="1.76854"
                  transform="matrix(-0.888791 -0.458312 -0.388549 0.921428 353.082 785.875)"
                  fill="url(#paint13_linear)"
                  fillOpacity="0.4"
                />
                <rect
                  width="3.53709"
                  height="148.503"
                  rx="1.76854"
                  transform="matrix(-0.888792 -0.458312 -0.388549 0.921428 574.189 608.381)"
                  fill="url(#paint14_linear)"
                  fillOpacity="0.4"
                />
                <rect
                  width="3.53709"
                  height="148.503"
                  rx="1.76854"
                  transform="matrix(-0.888792 -0.458312 -0.388549 0.921428 403.855 497.9)"
                  fill="url(#paint15_linear)"
                  fillOpacity="0.4"
                />
                <path
                  d="M104.706 632.116C87.5093 588.648 87.5089 524.352 87.5089 524.352L104.706 312.446L172.676 153.064L725.444 126.802H905.605L940 367.686L786.044 480.884C786.044 480.884 666.483 750.747 649.285 768.859C632.087 786.971 442.099 880.246 395.421 876.623C348.743 873.001 121.903 675.584 104.706 632.116Z"
                  fill="#6079B9"
                  fillOpacity="0.2"
                />
                <path
                  d="M548.234 897.393C504.67 910.317 440.137 903.693 440.137 903.693L227.389 864.455L67.1693 779.246L38.7664 217.133L38.1 34.8078L279.743 24.8199L393.926 192.289C393.926 192.289 665.223 341.092 683.465 360.362C701.707 379.633 796.027 581.514 792.564 628.379C789.101 675.244 591.798 884.468 548.234 897.393Z"
                  fill="#6079B9"
                  fillOpacity="0.2"
                />
                <path
                  d="M25.6538 652.552C9.92862 609.055 12.1441 544.871 12.1441 544.871L36.6709 333.44L110.238 174.751L664.762 151.914L845.2 153.015L871.346 393.689L713.252 505.748C713.252 505.748 584.206 774.41 566.358 792.384C548.509 810.359 355.014 902.31 308.389 898.409C261.765 894.508 41.3791 696.049 25.6538 652.552Z"
                  fill="#6079B9"
                  fillOpacity="0.4"
                />
                <defs>
                  <linearGradient
                    id="paint0_linear"
                    x1="1.76854"
                    y1="0"
                    x2="1.76854"
                    y2="148.503"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="white" stopOpacity="0.1" />
                    <stop offset="1" stopColor="white" />
                  </linearGradient>
                  <linearGradient
                    id="paint1_linear"
                    x1="1.76854"
                    y1="0"
                    x2="1.76854"
                    y2="148.503"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="white" stopOpacity="0.1" />
                    <stop offset="1" stopColor="white" />
                  </linearGradient>
                  <linearGradient
                    id="paint2_linear"
                    x1="1.76854"
                    y1="0"
                    x2="1.76854"
                    y2="148.503"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="white" stopOpacity="0.1" />
                    <stop offset="1" stopColor="white" />
                  </linearGradient>
                  <linearGradient
                    id="paint3_linear"
                    x1="1.76854"
                    y1="0"
                    x2="1.76854"
                    y2="148.503"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="white" stopOpacity="0.1" />
                    <stop offset="1" stopColor="white" />
                  </linearGradient>
                  <linearGradient
                    id="paint4_linear"
                    x1="1.76854"
                    y1="0"
                    x2="1.76854"
                    y2="148.503"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="white" stopOpacity="0.1" />
                    <stop offset="1" stopColor="white" />
                  </linearGradient>
                  <linearGradient
                    id="paint5_linear"
                    x1="1.76854"
                    y1="0"
                    x2="1.76854"
                    y2="148.503"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="white" stopOpacity="0.1" />
                    <stop offset="1" stopColor="white" />
                  </linearGradient>
                  <linearGradient
                    id="paint6_linear"
                    x1="1.76854"
                    y1="0"
                    x2="1.76854"
                    y2="148.503"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="white" stopOpacity="0.1" />
                    <stop offset="1" stopColor="white" />
                  </linearGradient>
                  <linearGradient
                    id="paint7_linear"
                    x1="1.76854"
                    y1="0"
                    x2="1.76854"
                    y2="148.503"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="white" stopOpacity="0.1" />
                    <stop offset="1" stopColor="white" />
                  </linearGradient>
                  <linearGradient
                    id="paint8_linear"
                    x1="1.76854"
                    y1="0"
                    x2="1.76854"
                    y2="148.503"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="white" stopOpacity="0.1" />
                    <stop offset="1" stopColor="white" />
                  </linearGradient>
                  <linearGradient
                    id="paint9_linear"
                    x1="1.76854"
                    y1="0"
                    x2="1.76854"
                    y2="148.503"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="white" stopOpacity="0.1" />
                    <stop offset="1" stopColor="white" />
                  </linearGradient>
                  <linearGradient
                    id="paint10_linear"
                    x1="1.76854"
                    y1="0"
                    x2="1.76854"
                    y2="148.503"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="white" stopOpacity="0.1" />
                    <stop offset="1" stopColor="white" />
                  </linearGradient>
                  <linearGradient
                    id="paint11_linear"
                    x1="1.76854"
                    y1="0"
                    x2="1.76854"
                    y2="148.503"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="white" stopOpacity="0.1" />
                    <stop offset="1" stopColor="white" />
                  </linearGradient>
                  <linearGradient
                    id="paint12_linear"
                    x1="1.76854"
                    y1="0"
                    x2="1.76854"
                    y2="148.503"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="white" stopOpacity="0.1" />
                    <stop offset="1" stopColor="white" />
                  </linearGradient>
                  <linearGradient
                    id="paint13_linear"
                    x1="1.76854"
                    y1="0"
                    x2="1.76854"
                    y2="148.503"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="white" stopOpacity="0.1" />
                    <stop offset="1" stopColor="white" />
                  </linearGradient>
                  <linearGradient
                    id="paint14_linear"
                    x1="1.76854"
                    y1="0"
                    x2="1.76854"
                    y2="148.503"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="white" stopOpacity="0.1" />
                    <stop offset="1" stopColor="white" />
                  </linearGradient>
                  <linearGradient
                    id="paint15_linear"
                    x1="1.76854"
                    y1="0"
                    x2="1.76854"
                    y2="148.503"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="white" stopOpacity="0.1" />
                    <stop offset="1" stopColor="white" />
                  </linearGradient>
                </defs>
              </svg>
            ) : weather.weather[0].icon === "01d" &&
              weather.weather[0].main === "Clear" ? (
              <svg
                version="1.1"
                id="Layer_1"
                xmlns="http://www.w3.org/2000/svg"
                x="0px"
                y="0px"
                viewBox="0 0 940 940"
                className="sun"
              >
                <g className="sungroup">
                  <circle className="st0" cx="470" cy="470" r="470" />
                  <circle className="st1" cx="470" cy="470" r="417.8" />
                  <circle className="st2" cx="470" cy="470" r="365.6" />
                </g>
              </svg>
            ) : (
              <svg
                viewBox="0 0 940 960"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="dark"
              >
                <g>
                  <circle cx="490" cy="486" r="470" fill="black" />
                </g>
              </svg>
            )
          ) : (
            <svg
              version="1.1"
              id="Layer_1"
              xmlns="http://www.w3.org/2000/svg"
              x="0px"
              y="0px"
              viewBox="0 0 940 940"
              className="sun"
            >
              <g className="sungroup">
                <circle className="st0" cx="470" cy="470" r="470" />
                <circle className="st1" cx="470" cy="470" r="417.8" />
                <circle className="st2" cx="470" cy="470" r="365.6" />
              </g>
            </svg>
          )}
        </div>
        <section className="mainbox">
          <div className="date">{dateBuilder(new Date())}</div>

          <main>
            <div className="search-box" ref={searchBoxRef}>
              <input
                type="text"
                className={`search-bar ${isInvalid ? "invalid" : ""}`}
                placeholder="Search Your Location"
                onChange={(e) => {
                  setQuery(e.target.value)
                  if (!e.target.value.trim()) {
                    setIsInvalid(false)
                    setShowDropdown(false)
                  }
                }}
                onFocus={() => {
                  if (query.trim() && (isInvalid || suggestions.length > 0)) {
                    setShowDropdown(true)
                  }
                }}
                onKeyDown={handleKeyDown}
                value={query}
              />
              {showDropdown && query.trim() !== "" && (
                <div className="suggestions-dropdown">
                  {isInvalid && (
                    <div className="dropdown-disclaimer error">
                      Location not found. Please check spelling or select from probable suggestions below.
                    </div>
                  )}
                  {!isInvalid && suggestions.length > 0 && (
                    <div className="dropdown-disclaimer info">
                      Suggested locations:
                    </div>
                  )}
                  {suggestions.map((item, index) => (
                    <div
                      key={`${item.lat}-${item.lon}-${index}`}
                      className="suggestion-item"
                      onClick={() => handleSelectSuggestion(item)}
                    >
                      <span className="suggestion-name">{item.name}</span>
                      <span className="suggestion-details">
                        {item.state && (
                          <span className="suggestion-state">{item.state}</span>
                        )}
                        {item.country && (
                          <span className="suggestion-country">
                            {item.state ? ", " : ""}{item.country}
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {typeof weather.main !== "undefined" ? (
              <div>
                <div className="location-box">
                  <div className="location">
                    <div className="direction">
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 15 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="arrow"
                      >
                        <circle cx="7.5" cy="7.5" r="7.5" fill="white" />
                        <path
                          d="M10.7782 4.12132L7.24265 11.8995L6.31224 8.58726L3.00001 7.65685L10.7782 4.12132Z"
                          fill="#313131"
                        />
                      </svg>
                    </div>

                    {typeof weather.main !== "undefined" ? (
                      <div>
                        <div className="locationtext">
                          {weather.name}, {weather.sys.country}
                        </div>
                        <div className="weather-box">
                          <div className="minmax">
                            {Math.round(weather.main.temp_min)}
                            {"°"} | {Math.round(weather.main.temp_max)}
                            {"°"}
                          </div>
                          <div className="temp">
                            {Math.round(weather.main.temp)}
                            <span style={{ color: 'rgb(238 141 141)'}}>°</span>
                          </div>
                          <div className="weather">
                            {weather.weather[0].description
                              .charAt(0)
                              .toUpperCase() +
                              weather.weather[0].description.slice(1)}
                          </div>
                        </div>

                        <div className="weatherfooter">
                          <div className="weatherdetailsicon">
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 18 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M8.99563 2L13.9481 6.68769C14.9275 7.61413 15.5947 8.79471 15.8652 10.0801C16.1356 11.3655 15.9973 12.6979 15.4676 13.9088C14.9379 15.1198 14.0407 16.1548 12.8895 16.8831C11.7382 17.6113 10.3847 18 9 18C7.61534 18 6.26178 17.6113 5.11053 16.8831C3.95929 16.1548 3.06209 15.1198 2.53241 13.9088C2.00273 12.6979 1.86437 11.3655 2.13484 10.0801C2.40531 8.79471 3.07245 7.61413 4.05188 6.68769L8.99563 2Z"
                                fill="white"
                                stroke="white"
                                strokeOpacity="0.34"
                                strokeWidth="4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <div className="weatherdetails">
                            {weather.main.humidity}% | Feels Like{" "}
                            {Math.round(weather.main.feels_like)}
                          </div>
                        </div>

                        {/* <div className="chart">
                          <Line data={data} options={options}></Line>
                        </div> */}

                        {/* <div className="week">
                          <div className="weekbar">
                            <div className="day">{dateBuilder1(new Date())}</div>
                            <img
                              src={`https://openweathermap.org/img/wn/${daily.daily[1].weather[0].icon}@2x.png`}
                              alt="icon"
                              className="week-icon"
                            ></img>
                            <div className="max">
                              {Math.round(daily.daily[1].temp.max)}
                              {"°"}
                            </div>
                            <div className="min">
                              {Math.round(daily.daily[1].temp.min)}
                              {"°"}
                            </div>
                          </div>
                          <div className="weekbar">
                            <div className="day">{dateBuilder2(new Date())}</div>
                            <img
                              src={`https://openweathermap.org/img/wn/${daily.daily[2].weather[0].icon}@2x.png`}
                              alt="icon"
                              className="week-icon"
                            ></img>
                            <div className="max">
                              {Math.round(daily.daily[2].temp.max)}
                              {"°"}
                            </div>
                            <div className="min">
                              {Math.round(daily.daily[2].temp.min)}
                              {"°"}
                            </div>
                          </div>
                          <div className="weekbar">
                            <div className="day">{dateBuilder3(new Date())}</div>
                            <img
                              src={`https://openweathermap.org/img/wn/${daily.daily[3].weather[0].icon}@2x.png`}
                              alt="icon"
                              className="week-icon"
                            ></img>
                            <div className="max">
                              {Math.round(daily.daily[3].temp.max)}
                              {"°"}
                            </div>
                            <div className="min">
                              {Math.round(daily.daily[3].temp.min)}
                              {"°"}
                            </div>
                          </div>
                          <div className="weekbar">
                            <div className="day">{dateBuilder4(new Date())}</div>
                            <img
                              src={`https://openweathermap.org/img/wn/${daily.daily[4].weather[0].icon}@2x.png`}
                              alt="icon"
                              className="week-icon"
                            ></img>
                            <div className="max">
                              {Math.round(daily.daily[4].temp.max)}
                              {"°"}
                            </div>
                            <div className="min">
                              {Math.round(daily.daily[4].temp.min)}
                              {"°"}
                            </div>
                          </div>
                          <div className="weekbar">
                            <div className="day">{dateBuilder5(new Date())}</div>
                            <img
                              src={`https://openweathermap.org/img/wn/${daily.daily[5].weather[0].icon}@2x.png`}
                              alt="icon"
                              className="week-icon"
                            ></img>
                            <div className="max">
                              {Math.round(daily.daily[5].temp.max)}
                              {"°"}
                            </div>
                            <div className="min">
                              {Math.round(daily.daily[5].temp.min)}
                              {"°"}
                            </div>
                          </div>
                          <div className="weekbar">
                            <div className="day">{dateBuilder6(new Date())}</div>
                            <img
                              src={`https://openweathermap.org/img/wn/${daily.daily[6].weather[0].icon}@2x.png`}
                              alt="icon"
                              className="week-icon"
                            ></img>
                            <div className="max">
                              {Math.round(daily.daily[6].temp.max)}
                              {"°"}
                            </div>
                            <div className="min">
                              {Math.round(daily.daily[6].temp.min)}
                              {"°"}
                            </div>
                          </div>
                        </div> */}
                      </div>
                    ) : (
                      <div>
                        <div>This city doesn't exsist</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div className="location-box">
                  <div className="location">
                    <div className="direction">
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 15 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="arrow"
                      >
                        <circle cx="7.5" cy="7.5" r="7.5" fill="white" />
                        <path
                          d="M10.7782 4.12132L7.24265 11.8995L6.31224 8.58726L3.00001 7.65685L10.7782 4.12132Z"
                          fill="#313131"
                        />
                      </svg>
                    </div>
                    Enter Your Location
                  </div>
                </div>
              </div>
            )}
          </main>
        </section>
      </div>
      <div className="footer">
        Powered by One weather <br />
        Copyright © 2021 Sashank Deb
      </div>
    </div>

  )
}

export default App
